import { useEffect, useId, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { glossary, type GlossaryKey } from '../content/glossary';
import styles from './HelpTip.module.css';

export interface HelpTipProps {
  /** Glossary key — title + content come from the shared glossary. */
  term?: GlossaryKey;
  /** Used when `term` is absent (explicit props always win). */
  title?: string;
  content?: ReactNode;
  placement?: 'bottom' | 'top';
  size?: 'sm' | 'md';
  /** Accessible-name override; defaults to `What is {title}?`. */
  label?: string;
  id?: string;
}

/**
 * Accessible inline help popover (Slice 1). A small "?" trigger beside a label or
 * heading reveals a short plain-language explanation. Non-modal: focus stays on the
 * trigger (never traps Tab); Esc / outside-click / re-click close it.
 */
export function HelpTip({ term, title, content, placement = 'bottom', size = 'sm', label, id }: HelpTipProps) {
  const autoId = useId();
  const rootId = id ?? autoId;
  const popoverId = `${rootId}-popover`;

  const entry = term ? glossary[term] : undefined;
  const resolvedTitle = title ?? entry?.title ?? '';
  const resolvedContent = content ?? entry?.short ?? '';

  const [open, setOpen] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] = useState<'bottom' | 'top'>(placement);
  const [inlineEnd, setInlineEnd] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const toggle = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        const rect = triggerRef.current?.getBoundingClientRect();
        const nearBottom = rect ? window.innerHeight - rect.bottom < 160 : false;
        setResolvedPlacement(placement === 'bottom' && nearBottom ? 'top' : placement);
        // Flip to the inline-end anchor when a start-anchored panel (~18rem) would
        // overflow the right edge — avoids horizontal scroll on narrow viewports.
        const panelWidth = 288;
        setInlineEnd(rect ? rect.left + panelWidth > window.innerWidth - 16 : false);
      }
      return !wasOpen;
    });
  };

  // Tidy close when focus leaves the trigger+popover (e.g. Tab away).
  const onBlur = (e: FocusEvent<HTMLSpanElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setOpen(false);
    }
  };

  if (!resolvedTitle || resolvedContent === '') {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('HelpTip requires a `term`, or both `title` and `content`.');
    }
    return null;
  }

  return (
    <span className={styles.root} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${size === 'md' ? styles.md : styles.sm}`}
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={label ?? `What is ${resolvedTitle}?`}
        onClick={toggle}
      >
        <span aria-hidden="true" className={styles.glyph}>
          ?
        </span>
      </button>
      {/* Toggle-tip: a persistent polite live region so the help text is ANNOUNCED
          when it appears (focus stays on the trigger — no trap). */}
      <span className={styles.live} role="status" aria-live="polite">
        {open ? (
          <span
            ref={popoverRef}
            id={popoverId}
            className={`${styles.popover} ${resolvedPlacement === 'top' ? styles.top : styles.bottom} ${inlineEnd ? styles.end : styles.start}`}
          >
            <span className={styles.title}>{resolvedTitle}</span>
            <span className={styles.body}>{resolvedContent}</span>
          </span>
        ) : null}
      </span>
    </span>
  );
}
